/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useContext, useEffect, useMemo } from 'react';

import { useNavigate, Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';

import logoMobile from '../../assets/images/snapsop2.png';

import { SocketContext } from '../../context';
import { UiContext } from '../../context/UiContext';
import { signUp } from '../../helpers/globalAuth';
import SplitTemplate from '../../components/SplitTemplate';
import UnderlinedInput from '../../components/Inputs/UnderlinedInput';
import ConfirmButton from '../../components/Buttons/ConfirmButton';
import classes from './styles.module.scss';
import useCheckMobileScreen from '../../hooks/useCheckMobileScreen';

const validationSchema = yup.object({
  email: yup
    .string()
    .trim()
    .email('Please enter a valid email')
    .required('This field is required'),
  password: yup
    .string()
    .required('This field is required')
    .min(8, 'The password should have at minimum length of 8'),
  confirmPassword: yup
    .string()
    .required('This field is required')
    .oneOf([yup.ref('password'), null], 'Passwords do not match'),
});

export default function SignUpPage() {
  const navigate = useNavigate();
  const { notifyError, redirectAfterLogin, setRedirectAfterLogin } =
    useContext(UiContext);
  const { socket } = useContext(SocketContext);
  const isMobile = useCheckMobileScreen();

  useEffect(() => {
    const shouldRedirectToDefaultPage = ['/my-videos', '/my-library'].includes(
      redirectAfterLogin
    );

    if (!redirectAfterLogin || shouldRedirectToDefaultPage) {
      setRedirectAfterLogin(isMobile ? '/my-videos' : '/my-library');
    }
  }, [isMobile, redirectAfterLogin, setRedirectAfterLogin]);

  const createUser = async (values) => {
    try {
      await signUp(
        {
          email: values.email,
          password: values.password,
        },
        socket
      );

      navigate('/onboarding');

      if (redirectAfterLogin) {
        setRedirectAfterLogin('');
      }
    } catch (error) {
      console.error(error);
      notifyError(error.message);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: createUser,
  });

  const hasError = useMemo(
    () => Object.values(formik.errors).some((error) => error),
    [formik.errors]
  );
  const hasEmptyValue = useMemo(
    () => Object.values(formik.values).some((value) => !value.length),
    [formik.values]
  );

  const isSubmitDisabled = hasError || hasEmptyValue;

  return (
    <div className={classes.SignUpPage}>
      <SplitTemplate>
        <div className={classes.logoContainer}>
          <img className={classes.logo} src={logoMobile} alt="SnapSOP" />
        </div>
        <div className={classes.content}>
          <span className={classes.signIn}>
            <Link to="/sign-in">Sign in</Link>
          </span>
          <h1>Sign up</h1>
          <form onSubmit={formik.handleSubmit}>
            <div className={classes.inputs}>
              <UnderlinedInput
                label={
                  formik.errors.email && formik.touched.email
                    ? formik.errors.email
                    : 'Your email'
                }
                value={formik.values.email}
                errors={formik.errors.email}
                isTouched={formik.touched.email}
                type="email"
                name="email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />

              <UnderlinedInput
                label={
                  formik.errors.password && formik.touched.password
                    ? formik.errors.password
                    : 'Your password'
                }
                value={formik.values.password}
                errors={formik.errors.password}
                isTouched={formik.touched.password}
                type="password"
                name="password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isPassword
              />

              <UnderlinedInput
                label={
                  formik.errors.confirmPassword && formik.touched.confirmPassword
                    ? formik.errors.confirmPassword
                    : 'Confirm password'
                }
                value={formik.values.confirmPassword}
                errors={formik.errors.confirmPassword}
                isTouched={formik.touched.confirmPassword}
                type="password"
                name="confirmPassword"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isPassword
                doPasswordsMatch={
                  formik.errors.confirmPassword !== 'Passwords do not match'
                }
              />
            </div>

            <div className={classes.row}>
            <p className={classes.termsPrivacyMessage}>
            By moving to the next step you accept our{' '}
            <a href="https://snapsop.com/terms-of-service" target="_blank" rel="noreferrer">
              Terms & Conditions
            </a>{' '}
            and{' '}
            <a href="https://snapsop.com/privacy-policy/" target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
          </p>
              <ConfirmButton
                isDisabled={isSubmitDisabled}
                withArrow
                width={isMobile ? 60 : ''}
              />
            </div>
          </form>
          <span className={classes.helperText}>
                Already have an account? <Link to="/sign-in">Sign in</Link>
              </span>
         
        </div>
      </SplitTemplate>
    </div>
  );
}
